/**
 * Enums for Badge Variants and Subvariants
 *
 * These enums define the different badge types (variants) and their available
 * options (subvariants). They ensure type safety and prevent errors by
 * avoiding hard-coded strings.
 *
 * Steps for Adding New Badge Variants/Subvariants:
 * 1. **Add a new variant**: Add a new entry to `BadgeVariantEnum` for the badge type
 *    (e.g., 'priority').
 * 2. **Define subvariants**: Create a new enum for the subvariant options of the
 *    new variant (e.g., 'priority' could have subvariants like 'high', 'low').
 * 3. **Update the map**: Modify `BadgeVariantSubvariantMap` to include the new
 *    variant and its subvariants.
 * 4. **Update the BadgeColorPalette**: Update the `badgeColorPalette` with the
 *    appropriate colors and opacity values.
 */

export enum BadgeVariantEnum {
  STATUS = 'status',
}

export enum BadgeStatusSubvariantEnum {
  APPROVED = 'approved',
  PENDING = 'pending',
  IN_REVIEW = 'in_review',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

export type BadgeVariantSubvariantMap = {
  [BadgeVariantEnum.STATUS]: BadgeStatusSubvariantEnum;
};

export type BadgeVariant<K extends keyof BadgeVariantSubvariantMap> = {
  variant: K;
  subvariant: BadgeVariantSubvariantMap[K];
};

export type BadgeVariants = {
  [K in keyof BadgeVariantSubvariantMap]: BadgeVariant<K>;
}[keyof BadgeVariantSubvariantMap];

export type BadgeColor = {
  bgHexColor: string;
  bgOpacity?: number;
  color?: string;
};

export type BadgeColorPalette = {
  [key in BadgeVariantEnum]: {
    [key in BadgeStatusSubvariantEnum]: BadgeColor;
  };
};
